import {
    PDFArray, PDFDocument, PDFName, PDFString, StandardFonts, rgb, type PDFFont, type RGB,
} from 'pdf-lib';
import dayjs from 'dayjs';
import { DateFormats } from '@/constants/DateFormats.ts';
import { SparkHelpdeskEmail } from '@/constants/ContactEmail.ts';
import { hexToRgbChannels } from '@/utils/colorUtils.ts';
import { formatPublicationDateForDisplay } from '@/utils/sparkContentUtils.ts';

interface IWatermarkOptions {
    userEmail: string;
    publicationDate: string;
    textColours?: Array<string | null>;
}

const FONT_SIZE = 6;
const LINE_HEIGHT = 7;
const MARGIN_X = 10;
const MARGIN_Y_FROM_TOP = 3;

const CONTACT_LINE_INDEX = 2;
const CONTACT_PREFIX = 'Contact ';

async function addWatermarkToPdf (bytes: Uint8Array, options: IWatermarkOptions): Promise<Uint8Array> {
    const { userEmail, publicationDate } = options;

    const pdfDoc = await PDFDocument.load(bytes);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    const downloadDate = dayjs().format(DateFormats.FULL_DAY_MONTH_YEAR);
    const downloadTime = new Date().toLocaleTimeString('en-gb', {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });
    const lines = [
        `This report was downloaded on Spark and created by LCP Delta on ${formatPublicationDateForDisplay(publicationDate)}.`,
        `Downloaded by: ${userEmail} on ${downloadDate} at ${downloadTime}`,
        `${CONTACT_PREFIX}${SparkHelpdeskEmail} for more information`,
    ];

    const emailLinkX = MARGIN_X + font.widthOfTextAtSize(CONTACT_PREFIX, FONT_SIZE);
    const emailLinkWidth = font.widthOfTextAtSize(SparkHelpdeskEmail, FONT_SIZE);

    const pages = pdfDoc.getPages();
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
        const page = pages[pageIndex];
        const { height } = page.getSize();
        const textColour = toPdfRgb(options.textColours?.[pageIndex]) || rgb(0.35, 0.35, 0.35);
        const textY = height - MARGIN_Y_FROM_TOP - FONT_SIZE;

        drawWatermarkLines(page, lines, font, textY, textColour);
        appendWatermarkLink(page, pdfDoc, emailLinkX, emailLinkWidth, textY);
    }

    return pdfDoc.save();
}

function toPdfRgb (hexColour?: string | null): RGB | null {
    if (!hexColour) return null;

    const channels = hexToRgbChannels(hexColour);
    if (!channels) return null;

    return rgb(channels.r / 255, channels.g / 255, channels.b / 255);
}

function drawWatermarkLines (
    page: ReturnType<PDFDocument['getPages']>[number],
    lines: Array<string>,
    font: PDFFont,
    textY: number,
    textColour: RGB,
): void {
    lines.forEach((line, index) => {
        page.drawText(line, {
            x: MARGIN_X,
            y: textY - index * LINE_HEIGHT,
            size: FONT_SIZE,
            font,
            color: textColour,
            opacity: 0.85,
        });
    });
}

function appendWatermarkLink (
    page: ReturnType<PDFDocument['getPages']>[number],
    pdfDoc: PDFDocument,
    emailLinkX: number,
    emailLinkWidth: number,
    textY: number,
): void {
    const emailY = textY - CONTACT_LINE_INDEX * LINE_HEIGHT;
    const linkAnnotRef = pdfDoc.context.register(
        pdfDoc.context.obj({
            Type: 'Annot',
            Subtype: 'Link',
            Rect: [emailLinkX, emailY - 1, emailLinkX + emailLinkWidth, emailY + FONT_SIZE],
            Border: [0, 0, 0],
            A: {
                Type: 'Action',
                S: 'URI',
                URI: PDFString.of(`mailto:${SparkHelpdeskEmail}`),
            },
        }),
    );

    const annots = page.node.Annots();
    if (annots) {
        annots.push(linkAnnotRef);
    } else {
        const annotArray = PDFArray.withContext(pdfDoc.context);
        annotArray.push(linkAnnotRef);
        page.node.set(PDFName.of('Annots'), annotArray);
    }
}

export { addWatermarkToPdf };
